export default {
  data() {
    return {
      scrollPosition: {
        x: 0,
        y: 0,
        top: 0,
        left: 0
      },
      scrollSubscribers: new Set(),
      scrollThrottleTimeout: null,
    };
  },
  mounted() {
    this.initScrollTracking();
  },
  beforeDestroy() {
    this.destroyScrollTracking();
  },
  methods: {
    initScrollTracking() {
      this.handleScroll = this.throttleScroll(this.updateScrollPosition, 16);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.updateScrollPosition();
    },
    destroyScrollTracking() {
      window.removeEventListener('scroll', this.handleScroll);
      this.scrollSubscribers.clear();
      if (this.scrollThrottleTimeout) {
        clearTimeout(this.scrollThrottleTimeout);
      }
    },
    updateScrollPosition() {
      const x = window.pageXOffset || document.documentElement.scrollLeft;
      const y = window.pageYOffset || document.documentElement.scrollTop;

      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      const documentWidth = Math.max(
        document.body.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth
      );

      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      const scrollableHeight = documentHeight - windowHeight;
      const scrollableWidth = documentWidth - windowWidth;

      const yPercent = scrollableHeight <= 0 ? 0 : Math.min(100, (y / scrollableHeight) * 100);
      const xPercent = scrollableWidth <= 0 ? 0 : Math.min(100, (x / scrollableWidth) * 100);

      const newPosition = {
        x,
        y,
        top: y,
        left: x,
        bottom: documentHeight - (y + windowHeight),
        right: documentWidth - (x + windowWidth),
        percentY: Math.round(yPercent * 100) / 100,
        percentX: Math.round(xPercent * 100) / 100
      };

      this.scrollPosition = newPosition;
      this.notifyScrollSubscribers(newPosition);
    },
    throttleScroll(func, delay) {
      return (...args) => {
        if (this.scrollThrottleTimeout) {
          return;
        }
        this.scrollThrottleTimeout = setTimeout(() => {
          func.apply(this, args);
          this.scrollThrottleTimeout = null;
        }, delay);
      };
    },
    subscribeToScroll(callback) {
      if (typeof callback !== 'function') {
        console.warn('ScrollTracker: Callback must be a function');
        return () => {};
      }

      this.scrollSubscribers.add(callback);
      callback(this.scrollPosition);

      return () => {
        this.scrollSubscribers.delete(callback);
      };
    },
    unsubscribeFromScroll(callback) {
      this.scrollSubscribers.delete(callback);
    },
    notifyScrollSubscribers(position) {
      this.scrollSubscribers.forEach(callback => {
        try {
          callback(position);
        } catch (error) {
          console.error('ScrollTracker: Error in subscriber callback:', error);
        }
      });
    },
    getScrollPosition() {
      return { ...this.scrollPosition };
    },
    isScrolledPast(threshold) {
      return this.scrollPosition.y > threshold;
    },
    getScrollPercentage() {
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      const windowHeight = window.innerHeight;
      const scrollableHeight = documentHeight - windowHeight;

      if (scrollableHeight <= 0) return 0;

      return Math.min(100, (this.scrollPosition.y / scrollableHeight) * 100);
    }
  },
};
